import React, { useCallback, useEffect, useMemo, useState } from "react";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Dialog } from "@/components/main/utils/Dialog";
import { Button } from "components/common/Button";
import { FormField } from "components/common/FormField";
import { Select } from "components/common/Select";
import { TextInput } from "components/common/TextInput";
import { LoadingState } from "components/common/LoadingState";
import { EmptyState } from "components/common/EmptyState";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import {
  createOrganization,
  deleteOrganization,
  listOrganizations,
  updateOrganization,
  type DatasetOrganization,
  type OrganizationInput,
} from "@/lib/api/datasets";
import {
  entityNameLooksValid,
  ORG_FIELDS,
  type OrgFieldDescriptor,
} from "@/components/main/datasets/organizationFields";

const EMPTY: OrganizationInput = {
  entity_type: "Organization",
  name: "",
  url: undefined,
  same_as: undefined,
  extra: {},
};

/// Read/write one descriptor's value against the draft, hiding whether it is a
/// typed column or an `extra` key. This is what lets the form loop `ORG_FIELDS`
/// instead of hard-coding inputs.
const readField = (draft: OrganizationInput, f: OrgFieldDescriptor): string =>
  f.core
    ? ((draft[f.key as "name" | "url" | "same_as" | "entity_type"] as
        string | undefined) ?? "")
    : (draft.extra[f.key] ?? "");

const writeField = (
  draft: OrganizationInput,
  f: OrgFieldDescriptor,
  value: string,
): OrganizationInput => {
  if (f.core) return { ...draft, [f.key]: value };
  const extra = { ...draft.extra };
  // Empty means "unset" — the server drops blanks anyway, and keeping them out
  // of the map means an untouched field never appears in the export.
  if (value.trim()) extra[f.key] = value;
  else delete extra[f.key];
  return { ...draft, extra };
};

const toInput = (org: DatasetOrganization): OrganizationInput => ({
  entity_type: org.entity_type,
  name: org.name,
  url: org.url,
  same_as: org.same_as,
  extra: { ...org.extra },
});

interface ManageOrganizationsDialogProps {
  open: boolean;
  onClose: () => void;
  /// Fired after any registry mutation so open creator pickers can refresh.
  onChanged?: () => void;
}

/// The registry maintenance surface: list every entity, add one, correct one.
/// Correcting a `same_as` here fixes it for every dataset pointing at the entry,
/// which is the reason this is a shared registry and not per-dataset fields.
export const ManageOrganizationsDialog: React.FC<
  ManageOrganizationsDialogProps
> = ({ open, onClose, onChanged }) => {
  const [orgs, setOrgs] = useState<DatasetOrganization[] | null>(null);
  const [draft, setDraft] = useState<OrganizationInput | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setOrgs(await listOrganizations());
    } catch {
      setOrgs([]);
      showNotificationGlobal("Failed to load organisations", "error");
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setDraft(null);
    setEditingId(null);
    void load();
  }, [open, load]);

  const nameValid = draft ? entityNameLooksValid(draft.name) : false;

  const save = useCallback(async () => {
    if (!draft || !nameValid) return;
    setSaving(true);
    try {
      if (editingId) await updateOrganization(editingId, draft);
      else await createOrganization(draft);
      setDraft(null);
      setEditingId(null);
      await load();
      onChanged?.();
    } catch (e) {
      const code = (e as { response?: { data?: { error?: string } } })?.response
        ?.data?.error;
      showNotificationGlobal(
        code === "organization_exists"
          ? "An organisation with that name already exists"
          : "Failed to save organisation",
        "error",
      );
    } finally {
      setSaving(false);
    }
  }, [draft, nameValid, editingId, load, onChanged]);

  const remove = useCallback(
    async (org: DatasetOrganization) => {
      try {
        await deleteOrganization(org.id);
        await load();
        onChanged?.();
      } catch {
        showNotificationGlobal("Failed to delete organisation", "error");
      }
    },
    [load, onChanged],
  );

  const rows = useMemo(() => orgs ?? [], [orgs]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="wide"
      autoSpacing={false}
      backdropStyle={{ paddingLeft: "var(--main-sidebar-width, 0px)" }}
      title={
        <div className="min-w-0">
          <p className="text-fg-bold">Organisations</p>
          <p
            className="text-fg-muted"
            style={{ fontSize: "var(--font-size-small)" }}
          >
            Entities a public dataset can credit as its creator
          </p>
        </div>
      }
      actions={
        <Button variant="default" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="flex flex-col gap-4 px-5 py-4 select-text cursor-auto">
        <p
          className="text-fg-muted"
          style={{ fontSize: "var(--font-size-small)", maxWidth: "78ch" }}
        >
          A creator says <em>who made the data</em> and is exported as a
          resolvable entity, separate from a dataset&apos;s attribution — which
          is the credit text a licence requires a reuser to display. Editing an
          entry here updates every dataset that credits it.
        </p>

        {orgs === null ? (
          <LoadingState label="Loading organisations…" />
        ) : (
          <>
            {rows.length === 0 && !draft ? (
              <EmptyState
                title="No organisations yet"
                subtitle="Add the bodies that author your public datasets — ISO, a national statistics office, Mycorr."
              />
            ) : (
              <div className="rounded-app border border-border overflow-hidden">
                {rows.map((org, i) => (
                  <div
                    key={org.id}
                    className={`group flex items-center gap-3 px-3 py-2 ${
                      i > 0 ? "border-t border-border" : ""
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="truncate">{org.name}</span>
                        <span
                          className="text-fg-muted shrink-0"
                          style={{ fontSize: "var(--font-size-small)" }}
                        >
                          {org.entity_type}
                        </span>
                      </div>
                      {(org.same_as || org.url) && (
                        <div
                          className="text-fg-muted truncate"
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: "var(--font-size-small)",
                          }}
                        >
                          {org.same_as ?? org.url}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="default"
                      size="xs"
                      onClick={() => {
                        setEditingId(org.id);
                        setDraft(toInput(org));
                      }}
                      aria-label={`Edit ${org.name}`}
                    >
                      <EditOutlinedIcon className="icon-xs" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="xs"
                      onClick={() => void remove(org)}
                      aria-label={`Delete ${org.name}`}
                    >
                      <DeleteOutlineOutlinedIcon className="icon-xs" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {draft ? (
              <div className="rounded-app border border-border p-4 flex flex-col gap-3">
                <p className="text-fg-bold">
                  {editingId ? "Edit organisation" : "New organisation"}
                </p>
                {/* Rendered from ORG_FIELDS — a new schema.org property needs an
                    entry there and nothing here. */}
                {ORG_FIELDS.map((f) => {
                  const value = readField(draft, f);
                  return (
                    <FormField
                      key={f.key}
                      label={f.label}
                      required={f.required}
                      helper={f.help}
                    >
                      {f.kind === "select" ? (
                        <Select
                          value={value}
                          onChange={(v) => setDraft(writeField(draft, f, v))}
                          options={(f.options ?? []).map((o) => ({
                            value: o,
                            label: o,
                          }))}
                          ariaLabel={f.label}
                        />
                      ) : (
                        <TextInput
                          value={value}
                          onChange={(v) => setDraft(writeField(draft, f, v))}
                          placeholder={f.placeholder}
                        />
                      )}
                    </FormField>
                  );
                })}
                <div className="flex items-center gap-2 justify-end">
                  <Button
                    variant="default"
                    onClick={() => {
                      setDraft(null);
                      setEditingId(null);
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => void save()}
                    disabled={!nameValid}
                    loading={saving}
                  >
                    {editingId ? "Save" : "Add"}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Button
                  variant="default"
                  onClick={() => {
                    setEditingId(null);
                    setDraft({ ...EMPTY, extra: {} });
                  }}
                >
                  <AddOutlinedIcon className="icon-xs" />
                  New organisation
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
